/** One vertex of a window opening, in the host surface (u, v) frame, metres. */
export interface WindowVertex {
  u: number;
  v: number;
}

/** AABB + optional outline — the fields `syncWindowGeometry` reads. */
export interface WindowGeom {
  u: number;
  v: number;
  w: number;
  h: number;
  polygon?: WindowVertex[];
  thickness?: number;
  material?: string;
  /** Frame border width inside the opening, metres. Visual-only (3D trim). */
  frameWidth?: number;
  /** A `WINDOW_FRAME_OPTIONS` id, NOT an acoustic catalog key. Visual-only. */
  frameMaterial?: string;
}

/**
 * Default glazed-opening catalog key.
 * TODO(glass-curve): swap or extend when thinner / IGU octave-band data lands
 * (see backend/docs/TODO.md). Physics today is this one curve for every window.
 */
export const WINDOW_GLASS_MATERIAL = 'glass_heavy';

/** Glass/frame thickness into the room, metres. Coplanar stamp ignores this;
 *  visual raytrace uses it as the inset so nearest-hit prefers glass. */
export const WINDOW_THICKNESS_DEFAULT_M = 0.02;

/** Frame border width inside the opening, metres. Visual-only (3D trim);
 *  the solver still sees the whole opening as glass. */
export const WINDOW_FRAME_WIDTH_DEFAULT_M = 0.05;

/** Max frame border width, metres. `syncWindowGeometry` clamps to [0, this]. */
export const WINDOW_FRAME_WIDTH_MAX_M = 0.20;

/** Default frame finish id — see `WINDOW_FRAME_OPTIONS`. */
export const WINDOW_FRAME_MATERIAL_DEFAULT = 'wood';

/** A paint finish for the window frame trim — a render color, not an
 *  acoustic material. `acoustic` is an existing backend wall-catalog key
 *  for future physics use, set only where one honestly exists today. */
export interface WindowFrameOption {
  id: string;
  label: string;
  /** sRGB hex render color. */
  color: string;
  acoustic?: string;
}

// White and aluminium carry no `acoustic` key until measured octave bands
// land for them (same TODO precedent as WINDOW_GLASS_MATERIAL above —
// see backend/docs/TODO.md's glass-curve note).
export const WINDOW_FRAME_OPTIONS: WindowFrameOption[] = [
  { id: 'wood', label: 'Wood', color: '#8a5a33', acoustic: 'wood_paneling' },
  { id: 'wood_dark', label: 'Dark wood', color: '#5a3a22', acoustic: 'door_wood_solid' },
  { id: 'white', label: 'White', color: '#ece9e2' },
  { id: 'aluminium', label: 'Aluminium', color: '#9aa1a6' },
];

/** Frame option by id; unknown or absent ids fall back to the default. */
export function windowFrameOption(id: string | undefined): WindowFrameOption {
  return WINDOW_FRAME_OPTIONS.find((o) => o.id === id)
    ?? WINDOW_FRAME_OPTIONS.find((o) => o.id === WINDOW_FRAME_MATERIAL_DEFAULT)!;
}

export const WINDOW_SNAP_M = 0.05;

/** Paint finish for a window opening — stored in `surfaceColors`, not acoustic material. */
export function windowColorKey(windowId: string): string {
  return `window:${windowId}`;
}

export function roundSnapM(m: number, step = WINDOW_SNAP_M): number {
  if (!Number.isFinite(m)) return 0;
  return Math.round(m / step) * step;
}

export function rectPolygon(u: number, v: number, w: number, h: number): WindowVertex[] {
  const hu = w / 2;
  const hv = h / 2;
  return [
    { u: u - hu, v: v - hv },
    { u: u + hu, v: v - hv },
    { u: u + hu, v: v + hv },
    { u: u - hu, v: v + hv },
  ];
}

export function aabbFromPolygon(poly: readonly WindowVertex[]): {
  u: number; v: number; w: number; h: number;
} {
  const us = poly.map((p) => p.u);
  const vs = poly.map((p) => p.v);
  const u0 = Math.min(...us);
  const u1 = Math.max(...us);
  const v0 = Math.min(...vs);
  const v1 = Math.max(...vs);
  return { u: (u0 + u1) / 2, v: (v0 + v1) / 2, w: u1 - u0, h: v1 - v0 };
}

/** Opening outline in the host (u, v) frame. Legacy entries have no polygon. */
export function windowPolygon(win: WindowGeom): WindowVertex[] {
  if (win.polygon && win.polygon.length >= 3) return win.polygon;
  return rectPolygon(win.u, win.v, win.w, win.h);
}

/** Fills in the defaults every opening is guaranteed to carry, and rebuilds the
 *  AABB from the outline when there is one.
 *
 *  The return type spells out `thickness`/`material`/`frameWidth`/
 *  `frameMaterial` rather than just `T` because they are added here
 *  unconditionally: inferred from a literal that omits them (which is most
 *  call sites, and every test), a plain `T` describes a value this function
 *  never returns and reading any of those fields off the result is a type
 *  error. */
export function syncWindowGeometry<T extends WindowGeom>(
  win: T,
): T & { thickness: number; material: string; frameWidth: number; frameMaterial: string } {
  const thickness = win.thickness ?? WINDOW_THICKNESS_DEFAULT_M;
  const material = win.material ?? WINDOW_GLASS_MATERIAL;
  const rawWidth = win.frameWidth;
  const frameWidth = !Number.isFinite(rawWidth)
    ? WINDOW_FRAME_WIDTH_DEFAULT_M
    : Math.min(Math.max(rawWidth as number, 0), WINDOW_FRAME_WIDTH_MAX_M);
  const frameMaterial = windowFrameOption(win.frameMaterial).id;
  if (win.polygon && win.polygon.length >= 3) {
    return { ...win, ...aabbFromPolygon(win.polygon), thickness, material, frameWidth, frameMaterial };
  }
  return { ...win, thickness, material, frameWidth, frameMaterial, polygon: undefined };
}

/** Inset an outline toward its interior by `insetM` metres (the glass area
 *  inside the frame trim). Edge-normal miter offset: each vertex moves along
 *  the averaged inward normals of its two edges, scaled by
 *  1/dot(miter, edgeNormal). Winding is normalized to CCW via the signed
 *  area first. The inset clamps to 45% of the AABB min dimension so an
 *  oversize value stays non-degenerate; `insetM <= 0` returns a copy. */
export function insetWindowPolygon(poly: readonly WindowVertex[], insetM: number): WindowVertex[] {
  const copy = poly.map((p) => ({ u: p.u, v: p.v }));
  if (!(insetM > 0) || copy.length < 3) return copy;
  const us = copy.map((p) => p.u);
  const vs = copy.map((p) => p.v);
  const minDim = Math.min(Math.max(...us) - Math.min(...us), Math.max(...vs) - Math.min(...vs));
  if (!(minDim > 0)) return copy;
  const inset = Math.min(insetM, 0.45 * minDim);
  let area = 0;
  for (let i = 0; i < copy.length; i += 1) {
    const a = copy[i];
    const b = copy[(i + 1) % copy.length];
    area += a.u * b.v - b.u * a.v;
  }
  const pts = area < 0 ? [...copy].reverse() : copy;
  const out: WindowVertex[] = [];
  for (let i = 0; i < pts.length; i += 1) {
    const prev = pts[(i - 1 + pts.length) % pts.length];
    const curr = pts[i];
    const next = pts[(i + 1) % pts.length];
    const len0 = Math.hypot(curr.u - prev.u, curr.v - prev.v);
    const len1 = Math.hypot(next.u - curr.u, next.v - curr.v);
    // Left normals are the inward normals of a CCW outline.
    const n0x = len0 > 0 ? -(curr.v - prev.v) / len0 : 0;
    const n0y = len0 > 0 ? (curr.u - prev.u) / len0 : 0;
    const n1x = len1 > 0 ? -(next.v - curr.v) / len1 : 0;
    const n1y = len1 > 0 ? (next.u - curr.u) / len1 : 0;
    const mLen = Math.hypot(n0x + n1x, n0y + n1y);
    if (!(mLen > 1e-9)) {
      out.push({ u: curr.u + n0x * inset, v: curr.v + n0y * inset });
      continue;
    }
    const mx = (n0x + n1x) / mLen;
    const my = (n0y + n1y) / mLen;
    const denom = mx * n0x + my * n0y;
    if (!(denom > 1e-9)) {
      out.push({ u: curr.u + n0x * inset, v: curr.v + n0y * inset });
      continue;
    }
    const s = inset / denom;
    out.push({ u: curr.u + mx * s, v: curr.v + my * s });
  }
  return out;
}
